// notif.repo — baca/tandai notifikasi. RLS rw_notif membatasi cabang; filter target per-peran di view.
import { supabase } from '@core/supabase';
import { fetchAllChunked } from '@core/paging';

export interface NotifRow {
  id: number; target: string; outlet: string | null; text: string;
  ticket_no: string | null; read: boolean; created_at: string; tahap: string | null;
}

export async function listNotifs(): Promise<NotifRow[]> {
  const { data, error } = await supabase()
    .from('notifications').select('id,target,outlet,text,ticket_no,read,created_at,tahap')
    .order('created_at', { ascending: false }).limit(60);
  if (error) throw new Error(error.message);
  return (data ?? []) as NotifRow[];
}

/** Notif MANAGERIAL (target 'SPV' = eskalasi/assign/approval + reminder pribadi 'EMP:<own>') — fetch TERPISAH
 *  agar tak terkubur cap-60 campuran (review F1/F2). RLS rw_notif tetap batasi cabang. empId = number (aman .or). */
export async function listManagerial(empId: number): Promise<NotifRow[]> {
  const { data, error } = await supabase()
    .from('notifications').select('id,target,outlet,text,ticket_no,read,created_at,tahap')
    .or(`target.eq.SPV,target.eq.EMP:${empId}`)
    .order('created_at', { ascending: false }).limit(60);
  if (error) throw new Error(error.message);
  return (data ?? []) as NotifRow[];
}

export async function markRead(ids: number[]): Promise<void> {
  if (!ids.length) return;
  const { error } = await supabase().from('notifications').update({ read: true }).in('id', ids);
  if (error) throw new Error(error.message);
}

/** Status tiket saat ini — untuk notifGoto (lompat ke menu antrian yang tepat).
 *  maybeSingle(): tiket bisa 0-baris (terhapus/di-arsip/RLS) — itu hasil SAH (null), bukan error.
 *  Source: postgrest-js PostgrestTransformBuilder JSDoc — "maybeSingle: zero or one row ... returns null when given 0 rows". */
export async function ticketStatus(no: string): Promise<string | null> {
  const { data } = await supabase().from('tickets').select('status').eq('no', no).maybeSingle();
  return (data as { status: string } | null)?.status ?? null;
}

/**
 * Status terkini beberapa tiket dalam SATU query (hindari N+1 saat menyaring notif selesai).
 * Tiket yang hilang (arsip/RLS/terhapus) tak muncul di peta → notif tugasnya dianggap selesai.
 */
export async function ticketStatuses(nos: string[]): Promise<Map<string, string>> {
  const uniq = [...new Set(nos.filter(Boolean))];
  if (!uniq.length) return new Map();
  const data = await fetchAllChunked<{ no: string; status: string }>(uniq,
    (bagian, a, b) => supabase().from('tickets').select('no,status').in('no', bagian).order('no').range(a, b));
  return new Map(data.map((r) => [r.no, r.status]));
}

/**
 * Apakah notif TUGAS masih perlu ditindak? Selesai bila ber-`tahap` tetapi status tiketnya
 * kini sudah berbeda (sudah maju). Notif tanpa tahap/tiket = informasi → selalu dianggap aktif
 * (hilangnya lewat penanda "dibaca"). Murni supaya bisa diuji apa adanya.
 */
export function notifOutstanding(n: Pick<NotifRow, 'tahap' | 'ticket_no'>, status: string | undefined): boolean {
  if (!n.tahap || !n.ticket_no) return true;
  return status === n.tahap;
}
